import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useModuleData } from "@/hooks/useModuleData";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { num } from "@/lib/format";
import api from "@/lib/api";
import { Mic, MicOff, Volume2, Check, SkipForward, PackageCheck, Waypoints, ChevronLeft, Layers, MapPin, Hash, ArrowRight, ScanLine, Printer, QrCode, X, Package, Box } from "lucide-react";
import { toast } from "sonner";
import { openPdf } from "@/lib/pdf";
import { Html5Qrcode } from "html5-qrcode";

const MEDIA = (pid, w = 400) => `${process.env.REACT_APP_BACKEND_URL}/api/shop/media/${pid}?w=${w}`;

// Live camera QR reader used to confirm the bin location during voice picking.
function QrScanner({ onDecode, onClose }) {
  const scanRef = useRef(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let stopped = false;
    const el = "pick-qr-reader";
    const scanner = new Html5Qrcode(el, { verbose: false });
    scanRef.current = scanner;
    scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 220, height: 220 } },
      (text) => { if (!stopped) onDecode(text); }, () => {})
      .catch(() => setErr("Could not start the camera. Allow camera access, or open the app in a full browser tab."));
    return () => {
      stopped = true;
      try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch (e) { /* no-op */ }
    };
    // eslint-disable-next-line
  }, []);
  return (
    <div data-testid="picker-qr-scanner" className="mt-3 rounded-xl border-2 border-[#0f766e] bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0f766e]"><QrCode size={14} /> Point the camera at the bin QR</span>
        <button type="button" data-testid="picker-qr-close" onClick={onClose} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-[#6a6d70] hover:bg-[#f0f1f4]"><X size={14} /> Close</button>
      </div>
      <div id="pick-qr-reader" className="overflow-hidden rounded-lg" />
      {err && <p className="mt-2 text-center text-[11px] text-[#bb0000]">{err}</p>}
    </div>
  );
}

const WORDS = { zero: 0, oh: 0, o: 0, nought: 0, one: 1, won: 1, two: 2, to: 2, too: 2, three: 3, four: 4, for: 4, fore: 4, five: 5, six: 6, sex: 6, seven: 7, eight: 8, ate: 8, nine: 9 };
const TEENS = { ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19 };
const TENS = { twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
// Convert a spoken phrase to a digit string. Handles numerals, single digits,
// teens (16), tens + unit ("seventy seven" -> 77) and "double/triple X".
const parseDigits = (s) => {
  const out = [];
  const toks = (s || "").toLowerCase().split(/[\s-]+/).filter(Boolean);
  for (let i = 0; i < toks.length; i++) {
    const t = toks[i];
    if (t === "double" || t === "triple") {
      const n = toks[i + 1];
      const dig = /^\d$/.test(n) ? n : (n in WORDS ? String(WORDS[n]) : null);
      if (dig) { out.push(dig.repeat(t === "double" ? 2 : 3)); i++; }
      continue;
    }
    if (/^\d+$/.test(t)) { out.push(t); continue; }
    if (t in TEENS) { out.push(String(TEENS[t])); continue; }
    if (t in TENS) {
      const n = toks[i + 1];
      if (n && n in WORDS && WORDS[n] >= 1 && WORDS[n] <= 9) { out.push(String(TENS[t] + WORDS[n])); i++; }
      else { out.push(String(TENS[t])); }
      continue;
    }
    if (t in WORDS) { out.push(String(WORDS[t])); }
  }
  return out.join("");
};
const spell = (s) => (s || "").split("").join(" ");

export default function Picking() {
  const { data: waves, loading: wl, reload: reloadWaves } = useModuleData("/pick-waves");
  const { data: deliveries, loading: dl, reload: reloadDeliveries } = useModuleData("/deliveries");
  const [waveId, setWaveId] = useState(null); // null=selection, "ALL"=all open, or a wave id
  const [index, setIndex] = useState(0);
  const [step, setStep] = useState("loc"); // loc -> qty -> next
  const [digits, setDigits] = useState("");
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState(null);
  const [scanMode, setScanMode] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [qrMode, setQrMode] = useState(false);
  const [prod, setProd] = useState(null);
  const qrToastRef = useRef(0);
  const [confThreshold, setConfThreshold] = useState(0.4);
  const [promptsOn, setPromptsOn] = useState(() => { try { return localStorage.getItem("pick_prompts") !== "off"; } catch (e) { return true; } });
  const [testDigits, setTestDigits] = useState(null);
  const recogRef = useRef(null);
  const completedRef = useRef(null);
  const processingRef = useRef(false);
  const firstTryRef = useRef(true);
  const speakingRef = useRef(false);
  const micWanted = useRef(false);
  const trainingRef = useRef(null);
  const trainingDigitsRef = useRef("");
  const trainingTimerRef = useRef(null);
  const voiceRef = useRef(() => {});
  const locDigitsRef = useRef("");
  const lastLocSubmitRef = useRef(0);
  const speakSeqRef = useRef(0);
  const promptsOnRef = useRef(true);

  const openWaves = (waves || []).filter((w) => w.status === "OPEN");
  const selectedWave = waveId && waveId !== "ALL" ? (waves || []).find((w) => w.id === waveId) : null;
  const queue = waveId === "ALL"
    ? (deliveries || []).filter((d) => d.status === "CREATED")
    : (selectedWave?.deliveries || []).filter((d) => d.status === "CREATED");
  const current = queue[index] || null;

  // 4-digit rack check code derived from the bin; operator confirms the last 2.
  const checkCode = useMemo(() => {
    const s = current?.from_bin || "";
    let h = 0;
    for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) % 10000;
    return String(h).padStart(4, "0");
  }, [current?.from_bin]);
  const expected2 = checkCode.slice(-2);
  const inIframe = typeof window !== "undefined" && window.self !== window.top;

  const speak = useCallback((text) => {
    try {
      // Silent mode (recommended for AirPods / Bluetooth headsets): skip TTS entirely so the
      // spoken prompt can't hijack the Bluetooth audio channel and stop the recogniser.
      if (!promptsOnRef.current) { try { window.speechSynthesis?.cancel(); } catch (e) { /* no-op */ } speakingRef.current = false; return; }
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel();
      speakingRef.current = true;
      const seq = ++speakSeqRef.current;
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0; u.pitch = 1.0; u.lang = "en-GB";
      let done = false;
      const clear = () => {
        // Ignore a stale watchdog if a newer prompt has since started speaking.
        if (done || speakSeqRef.current !== seq) return;
        done = true;
        speakingRef.current = false;
        // Keep the recogniser alive across prompts; if iOS auto-ended it during TTS, restart it (harmless if already running).
        if (micWanted.current && recogRef.current) { try { recogRef.current.start(); } catch (e) { /* already running */ } }
      };
      // Short cooldown after the prompt so the tail of the TTS is not transcribed as input.
      u.onend = () => setTimeout(clear, 400);
      u.onerror = () => clear();
      synth.speak(u);
      // Watchdog: iOS Safari frequently never fires onend for TTS started outside a user gesture,
      // which would otherwise leave speakingRef stuck true and gate the mic forever. Force-clear.
      setTimeout(clear, Math.min(9000, 1800 + (text || "").length * 90));
    } catch (e) { speakingRef.current = false; }
  }, []);

  const currentInstruction = !current
    ? "All pick tasks complete. Great job."
    : step === "loc"
      ? `Go to bin ${spell(current.from_bin)}. Read back the last two check digits.`
      : step === "qty"
        ? `Pick ${num(current.qty)} ${current.uom || "units"}. Say the quantity, or say confirm.`
        : "Say next for the next pick.";

  // Task entry / auto-advance: reset to location step and announce the bin.
  useEffect(() => {
    if (!waveId || !current) return;
    setStep("loc"); setDigits(""); firstTryRef.current = true; locDigitsRef.current = ""; setScanMode(false); setScanValue(""); setQrMode(false);
    speak(`Pick ${index + 1} of ${queue.length}. Go to bin ${spell(current.from_bin)}. Read back the last two check digits.`);
    // eslint-disable-next-line
  }, [current?.id, waveId]);

  // Fetch the product master (image + pack/size) for the current pick so the operator can
  // visually confirm they have the right product — single source of truth from PIM.
  useEffect(() => {
    if (!current?.material_code) { setProd(null); return; }
    setProd(null);
    const qs = `material_code=${encodeURIComponent(current.material_code)}${current.site_id ? `&site=${encodeURIComponent(current.site_id)}` : ""}`;
    api.get(`/pick/product-info?${qs}`).then((r) => setProd(r.data)).catch(() => setProd(null));
  }, [current?.material_code, current?.site_id]);

  // auto-complete a real wave once all its deliveries are picked
  useEffect(() => {
    if (selectedWave && selectedWave.open === 0 && selectedWave.status === "OPEN" && completedRef.current !== selectedWave.id) {
      completedRef.current = selectedWave.id;
      api.post(`/pick-waves/${selectedWave.id}/complete`).then(() => reloadWaves()).catch(() => {});
    }
  }, [selectedWave, reloadWaves]);

  const submitLoc = useCallback((val) => {
    if (!current) return;
    const d = String(val || "").replace(/\D/g, "").slice(-2);
    if (d.length === 2 && d === expected2) {
      locDigitsRef.current = "";
      toast.success(`Location confirmed · check digits ${d}`);
      speak(`Location confirmed. Pick ${num(current.qty)} ${current.uom || "units"}. Say confirm.`);
      setStep("qty"); setDigits("");
    } else {
      locDigitsRef.current = "";
      firstTryRef.current = false;
      toast.error(`Check digits ${d || "—"} do not match`);
      speak("Wrong location. Read the two check digits again.");
    }
  }, [current, expected2, speak]);

  // Barcode fallback for noisy areas — scan/type the bin code (handheld guns act as a keyboard + Enter).
  const scanBin = (val) => {
    if (!current) return;
    const s = String(val || "").trim().toUpperCase();
    if (!s) return;
    if (s === (current.from_bin || "").toUpperCase()) {
      locDigitsRef.current = "";
      toast.success(`Bin ${current.from_bin} scanned`);
      speak(`Location confirmed. Pick ${num(current.qty)} ${current.uom || "units"}. Say confirm.`);
      setStep("qty"); setDigits(""); setScanMode(false); setScanValue("");
    } else {
      firstTryRef.current = false;
      toast.error(`Scanned ${s} — expected bin ${current.from_bin}`);
      setScanValue("");
    }
  };

  // QR camera decode → confirm the bin. Throttle mismatch feedback since the reader fires continuously.
  const onQrDecode = (text) => {
    if (!current) return;
    const s = String(text || "").trim().toUpperCase();
    if (s === (current.from_bin || "").toUpperCase()) {
      setQrMode(false);
      scanBin(text);
    } else if (Date.now() - qrToastRef.current > 2000) {
      qrToastRef.current = Date.now();
      firstTryRef.current = false;
      toast.error(`Scanned ${s} — expected bin ${current.from_bin}`);
    }
  };

  const renderProductCard = (compact = false) => {
    if (!current) return null;
    const img = prod?.has_image && prod?.pid ? MEDIA(prod.pid, 400) : null;
    const badUom = (v) => !v || ["YES", "NO", "N/A"].includes(String(v).toUpperCase());
    const packBits = prod ? [
      !badUom(prod.order_uom) && `Pack UOM ${prod.order_uom}`,
      prod.min_order_qty && `Min ${num(prod.min_order_qty)}`,
      prod.gross_weight && `${prod.gross_weight} ${prod.weight_unit || "KG"}`,
    ].filter(Boolean) : [];
    return (
      <div data-testid="picker-product-card" className={`flex gap-3 rounded-xl border border-[#e5e9f0] bg-white ${compact ? "p-2" : "p-3"}`}>
        <div className={`flex ${compact ? "h-16 w-16" : "h-24 w-24"} flex-none items-center justify-center overflow-hidden rounded-lg border border-[#e5e9f0] bg-[#f7f8fa]`}>
          {img ? (
            <img data-testid="picker-product-image" src={img} alt={prod?.name || current.material_name} className="h-full w-full object-contain" />
          ) : (
            <Package size={compact ? 22 : 34} className="text-[#c8cfda]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div data-testid="picker-product-name" className={`font-semibold text-[#32363a] ${compact ? "text-sm line-clamp-1" : "line-clamp-2"}`}>{prod?.name || current.material_name}</div>
          <div className="mt-0.5 font-mono text-[11px] text-[#89919a]">{current.material_code}</div>
          {!compact && prod?.description && <div data-testid="picker-product-desc" className="mt-1 text-xs text-[#6a6d70] line-clamp-2">{prod.description}</div>}
          {packBits.length > 0 && (
            <div data-testid="picker-product-pack" className="mt-1.5 flex flex-wrap gap-1">
              {packBits.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-[#eef1f5] px-2 py-0.5 text-[10px] font-semibold text-[#454b52]"><Box size={10} /> {b}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const confirmQty = useCallback(() => {
    if (!current) return;
    toast.success(`Quantity ${num(current.qty)} confirmed`);
    speak("Quantity confirmed. Say next to complete this pick.");
    setStep("next");
  }, [current, speak]);

  const goNext = useCallback(async () => {
    if (!current || processingRef.current) return;
    processingRef.current = true;
    try {
      await api.post(`/deliveries/${current.id}/pick?first_try=${firstTryRef.current}`);
      toast.success(`Picked ${current.delivery_no}`);
      speak("Confirmed. Next pick.");
      await Promise.all([reloadDeliveries(), reloadWaves()]);
    } catch (e) { toast.error("Pick failed"); speak("Error. Please retry."); }
    finally { setTimeout(() => { processingRef.current = false; }, 900); }
  }, [current, reloadDeliveries, reloadWaves, speak]);

  const skip = useCallback(() => {
    speak("Skipped."); setDigits(""); setStep("loc"); setScanMode(false); setScanValue(""); setQrMode(false);
    setIndex((i) => Math.min(i + 1, Math.max(0, queue.length - 1)));
  }, [queue.length, speak]);

  // Single voice router (kept in a ref to avoid stale closures inside SpeechRecognition).
  voiceRef.current = (said, conf, isFinal = true) => {
    if (trainingRef.current) {
      const exp = trainingRef.current;
      const acc = (trainingDigitsRef.current + parseDigits(said)).slice(-4);
      trainingDigitsRef.current = acc;
      if (acc.slice(-2) === exp || acc.includes(exp)) {
        trainingRef.current = null; trainingDigitsRef.current = "";
        if (trainingTimerRef.current) { clearTimeout(trainingTimerRef.current); trainingTimerRef.current = null; }
        setTestDigits(null);
        toast.success(`Voice test passed — heard ${exp} clearly`);
      }
      return;
    }
    if (isFinal && (said.includes("repeat") || said.includes("again"))) { speak(currentInstruction); return; }
    if (isFinal && said.includes("skip")) { skip(); return; }
    if (step === "loc") {
      const d = parseDigits(said);
      if (!d) return;
      const lowConf = typeof conf === "number" && conf > 0 && conf < confThreshold;
      if (lowConf) { if (isFinal) speak("I didn't catch that clearly. Please read the two check digits again."); return; }
      // Fast-path: accept the instant the transcript (even an interim one, as on iPhone) reads the expected digits.
      if (d.slice(-2) === expected2 && Date.now() - lastLocSubmitRef.current > 1500) {
        lastLocSubmitRef.current = Date.now(); locDigitsRef.current = ""; submitLoc(expected2); return;
      }
      if (!isFinal) return;
      // iOS Safari often returns one digit per result — accumulate across finals until we have two.
      if (Date.now() - lastLocSubmitRef.current < 1200) return;
      locDigitsRef.current = (locDigitsRef.current + d).slice(-4);
      if (locDigitsRef.current.length >= 2) {
        const two = locDigitsRef.current.slice(-2);
        lastLocSubmitRef.current = Date.now(); locDigitsRef.current = "";
        submitLoc(two);
      }
      return;
    }
    if (!isFinal) return;
    if (step === "qty") {
      if (said.includes("confirm") || said.includes("yes") || said.includes("correct") || parseDigits(said).includes(String(current?.qty))) confirmQty();
    } else if (step === "next") {
      if (said.includes("next") || said.includes("confirm") || said.includes("continue") || said.includes("yes")) goNext();
    }
  };

  const startRecog = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = "en-GB"; r.continuous = true; r.interimResults = true;
    r.onresult = (ev) => {
      const res = ev.results[ev.results.length - 1];
      const txt = res[0].transcript.trim();
      setHeard({ text: txt, conf: res[0].confidence, final: res.isFinal });
      if (speakingRef.current || processingRef.current) return;
      voiceRef.current(txt.toLowerCase(), res[0].confidence, res.isFinal);
    };
    r.onerror = (e) => {
      // 'aborted' / 'no-speech' are transient (fired while TTS speaks or on auto-restart) — ignore and let onend restart.
      if (e.error === "aborted" || e.error === "no-speech") return;
      const map = {
        "not-allowed": "Microphone blocked. If the app is inside a preview frame, open it in its own browser tab and allow mic access.",
        "service-not-allowed": "Microphone blocked by the browser. Open the app in a full browser tab and allow mic access.",
        "audio-capture": "No microphone found on this device.",
      };
      const fatal = ["not-allowed", "service-not-allowed", "audio-capture"];
      toast.error(map[e.error] || `Voice error: ${e.error || "unknown"}`);
      if (fatal.includes(e.error)) { micWanted.current = false; setListening(false); }
    };
    r.onend = () => {
      if (micWanted.current && !speakingRef.current) { try { r.start(); return; } catch (x) { /* no-op */ } }
      setListening(false);
    };
    recogRef.current = r;
    try { r.start(); setListening(true); } catch (x) { toast.error("Could not start the microphone."); }
  };

  const saveSensitivity = async (v) => {
    setConfThreshold(v);
    try { await api.put("/picker-settings", { confidence: v }); toast.success("Voice sensitivity saved"); } catch { /* no-op */ }
  };

  const startTraining = () => {
    const t = String(Math.floor(Math.random() * 90) + 10);
    trainingRef.current = t; trainingDigitsRef.current = "";
    setTestDigits(`${t[0]} ${t[1]}`);
    if (trainingTimerRef.current) clearTimeout(trainingTimerRef.current);
    if (!micWanted.current) toggleMic();
    setTimeout(() => speak(`Voice test. Please read these two digits: ${t[0]} ${t[1]}.`), 500);
    trainingTimerRef.current = setTimeout(() => {
      if (trainingRef.current) {
        const exp = trainingRef.current; trainingRef.current = null; trainingDigitsRef.current = "";
        setTestDigits(null);
        toast.error(`Didn't catch ${exp}. Read the two digits together, e.g. "${exp[0]} ${exp[1]}". If you're on AirPods, see the tip below.`);
      }
    }, 12000);
  };

  const toggleMic = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice recognition isn't supported in this browser — use the buttons instead."); return; }
    if (listening || micWanted.current) { micWanted.current = false; try { recogRef.current?.abort(); } catch (e) { /* no-op */ } setListening(false); setHeard(null); return; }
    micWanted.current = true;
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch (err) {
      toast.error("Microphone access denied. If the app is inside a preview frame, open it in a new browser tab and allow the mic.");
      return;
    }
    startRecog();
  };

  useEffect(() => { promptsOnRef.current = promptsOn; try { localStorage.setItem("pick_prompts", promptsOn ? "on" : "off"); } catch (e) { /* no-op */ } }, [promptsOn]);

  useEffect(() => { api.get("/picker-settings").then((r) => setConfThreshold(r.data.confidence)).catch(() => {}); }, []);

  useEffect(() => () => { try { micWanted.current = false; recogRef.current?.abort(); window.speechSynthesis?.cancel(); } catch (e) { /* no-op */ } }, []);

  const exitWave = () => { micWanted.current = false; try { recogRef.current?.abort(); } catch (e) { /* no-op */ } setListening(false); setHeard(null); setWaveId(null); setIndex(0); setStep("loc"); setDigits(""); setScanMode(false); setScanValue(""); setQrMode(false); };

  const PromptToggle = () => (
    <div className="flex items-center justify-between rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-xs" data-testid="picker-prompts-toggle">
      <span className="font-semibold text-[#6a6d70]">Spoken prompts</span>
      <div className="flex gap-1">
        <button type="button" data-testid="prompts-voice" onClick={() => setPromptsOn(true)} className={`rounded-md px-2.5 py-1 font-semibold transition ${promptsOn ? "bg-[#DB2777] text-white" : "text-[#6a6d70] hover:bg-[#f0f1f4]"}`}>Voice</button>
        <button type="button" data-testid="prompts-silent" onClick={() => setPromptsOn(false)} className={`rounded-md px-2.5 py-1 font-semibold transition ${!promptsOn ? "bg-[#DB2777] text-white" : "text-[#6a6d70] hover:bg-[#f0f1f4]"}`}>Silent</button>
      </div>
    </div>
  );

  if (wl || dl) return <Loader />;

  // ---------- Wave selection screen ----------
  if (!waveId) {
    const allOpen = (deliveries || []).filter((d) => d.status === "CREATED").length;
    return (
      <div className="mx-auto max-w-md">
        <PageHeader title="Voice Picking" subtitle="Hands-free flow: confirm 2 check digits, confirm quantity, then say next." icon={Mic} accent="#DB2777" />
        <div className="space-y-3">
          <div data-testid="picker-voice-tuning" className="rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#6a6d70]"><Mic size={14} /> Voice sensitivity</div>
            <div className="flex gap-2">
              {[["High", 0.2], ["Medium", 0.4], ["Strict", 0.6]].map(([lbl, v]) => (
                <button key={lbl} data-testid={`voice-sens-${lbl}`} onClick={() => saveSensitivity(v)}
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-semibold transition ${Math.abs(confThreshold - v) < 0.01 ? "border-[#DB2777] bg-[#DB2777]/10 text-[#DB2777]" : "border-[#e5e9f0] text-[#6a6d70] hover:border-[#DB2777]/40"}`}>{lbl}</button>
              ))}
            </div>
            <button data-testid="voice-test-btn" onClick={startTraining} className="mt-2 w-full rounded-lg border border-[#c8cfda] py-2 text-xs font-semibold text-[#0f766e] hover:border-[#0f766e]">Test my voice (read-back check)</button>
            {testDigits && (
              <div data-testid="voice-test-digits" className="mt-2 rounded-lg border-2 border-[#DB2777] bg-[#DB2777]/5 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-[#89919a]">Read these two digits aloud</div>
                <div className="font-mono text-3xl font-bold tracking-[0.3em] text-[#DB2777]">{testDigits}</div>
                <div className="mt-1 text-[10px] text-[#89919a]">🎙️ Listening…</div>
              </div>
            )}
            <div className="mt-2"><PromptToggle /></div>
            <p className="mt-1 text-[10px] text-[#89919a]">Voice uses the phone's built-in mic. You can also <b>type</b> the two check digits, <b>Scan bin barcode</b>, or use the on-screen buttons.</p>
            {heard && <p className="mt-1 text-center text-[11px] text-[#6a6d70]">Heard: “{heard.text}”{typeof heard.conf === "number" && heard.conf > 0 ? ` · ${Math.round(heard.conf * 100)}%` : ""}</p>}
          </div>
          {openWaves.length === 0 && (
            <div data-testid="picker-no-waves" className="rounded-xl border border-[#e5e9f0] bg-white p-5 text-center text-sm text-[#6a6d70]">
              No open pick waves. Build one from Outbound Deliveries, or pick all open tasks below.
            </div>
          )}
          {openWaves.map((w) => (
            <div key={w.id} className="flex items-stretch gap-2">
              <button data-testid={`wave-start-${w.wave_no}`} onClick={() => { setWaveId(w.id); setIndex(0); }}
                className="flex flex-1 items-center justify-between rounded-xl border border-[#e5e9f0] bg-white p-4 text-left shadow-sm transition hover:border-[#DB2777]">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#DB2777]/10 text-[#DB2777]"><Waypoints size={18} /></div>
                  <div><div className="font-mono text-sm font-semibold text-[#32363a]">{w.wave_no}</div>
                    <div className="text-[11px] text-[#6a6d70]">{w.open} open / {w.total} tasks · site {w.site_id}</div></div>
                </div>
                <span className="text-sm font-semibold text-[#DB2777]">Start →</span>
              </button>
              <button data-testid={`wave-picklist-${w.wave_no}`} onClick={() => openPdf(`/print/pick-list/${w.id}.pdf`)} title="Print pick list (A4)"
                className="flex items-center justify-center rounded-xl border border-[#e5e9f0] bg-white px-3 text-[#6a6d70] shadow-sm transition hover:border-[#DB2777] hover:text-[#DB2777]"><Printer size={16} /></button>
            </div>
          ))}
          <button data-testid="pick-all-open" onClick={() => { setWaveId("ALL"); setIndex(0); }}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#c8cfda] bg-white p-3 text-sm font-semibold text-[#454b52] hover:border-[#DB2777]">
            <Layers size={16} /> Pick all open tasks ({allOpen})
          </button>
        </div>
      </div>
    );
  }

  const stepMeta = [
    { key: "loc", n: 1, label: "Check digits", icon: MapPin },
    { key: "qty", n: 2, label: "Quantity", icon: Hash },
    { key: "next", n: 3, label: "Next", icon: ArrowRight },
  ];
  const activeStepN = stepMeta.find((s) => s.key === step)?.n || 1;

  // ---------- Picking screen ----------
  return (
    <div className="mx-auto max-w-md">
      <PageHeader title="Voice Picking" subtitle={selectedWave ? `Wave ${selectedWave.wave_no}` : "All open pick tasks"} icon={Mic} accent="#DB2777" />
      <button data-testid="picker-back" onClick={exitWave} className="mb-3 inline-flex items-center gap-1 text-sm text-[#0a6ed1] hover:underline"><ChevronLeft size={16} /> Back to waves</button>
      <div className="mb-4 flex items-center justify-between rounded-lg border border-[#e5e9f0] bg-white px-4 py-2 text-sm">
        <span className="text-[#6a6d70]">Open pick tasks</span>
        <span data-testid="picker-remaining" className="font-mono font-semibold text-[#DB2777]">{queue.length}</span>
      </div>

      {!current ? (
        <div data-testid="picker-complete" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
          <PackageCheck size={40} className="mx-auto text-emerald-600" />
          <div className="mt-3 text-lg font-semibold text-emerald-700">Wave complete</div>
          <div className="text-sm text-emerald-600">All pick tasks confirmed.</div>
          <button data-testid="picker-back-done" onClick={exitWave} className="mt-4 rounded-lg bg-[#DB2777] px-4 py-2 text-sm font-semibold text-white">Back to waves</button>
        </div>
      ) : (
        <div data-testid="picker-current" className="rounded-2xl border border-[#e5e9f0] bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between text-xs text-[#6a6d70]">
            <span>Task {index + 1} / {queue.length}</span>
            <span className="font-mono">{current.delivery_no}</span>
          </div>

          {/* Step indicator */}
          <div data-testid="picker-step" data-step={step} className="mt-3 flex items-center justify-center gap-1.5">
            {stepMeta.map((s, i) => {
              const done = s.n < activeStepN;
              const active = s.n === activeStepN;
              return (
                <div key={s.key} className="flex items-center">
                  <div className={`flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold ${active ? "bg-[#DB2777] text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-[#f0f1f4] text-[#89919a]"}`}>
                    <s.icon size={12} /> {s.label}
                  </div>
                  {i < stepMeta.length - 1 && <div className={`mx-0.5 h-px w-4 ${done ? "bg-emerald-300" : "bg-[#e5e9f0]"}`} />}
                </div>
              );
            })}
          </div>

          <div className="mt-4 text-center">
            <div className="text-xs uppercase tracking-wide text-[#89919a]">Go to bin</div>
            <div data-testid="picker-bin" className="mt-1 font-mono text-4xl font-bold text-[#DB2777]">{current.from_bin}</div>
            <div data-testid="picker-checkcode" className="mt-1 font-mono text-xs text-[#89919a]">
              rack check code {checkCode.slice(0, 2)}<span className="font-bold text-[#DB2777]">{expected2}</span> — confirm last two
            </div>
          </div>

          {/* STEP 1 — location check digits */}
          {step === "loc" && (
            <>
            <div className="mt-4">{renderProductCard(true)}</div>
            <form data-testid="picker-loc-form" onSubmit={(e) => { e.preventDefault(); submitLoc(digits); }} className="mt-4">
              <div className="rounded-xl bg-[#f7f8fa] p-4 text-center">
                <div className="text-xs uppercase tracking-wide text-[#89919a]">Enter / say the last 2 check digits</div>
                <input data-testid="picker-digit-input" value={digits} inputMode="numeric" maxLength={2} autoFocus
                  onChange={(e) => setDigits(e.target.value.replace(/\D/g, "").slice(0, 2))}
                  placeholder="––"
                  className="mt-2 w-28 rounded-lg border border-[#c8cfda] bg-white py-2 text-center font-mono text-3xl tracking-[0.4em] outline-none focus:border-[#DB2777]" />
              </div>
              <button data-testid="picker-confirm-loc" type="submit"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#107e3e] py-3 text-base font-semibold text-white hover:bg-[#0c6a34]">
                <MapPin size={18} /> Confirm location
              </button>
            </form>
            <div className="mt-3 space-y-2">
              <button type="button" data-testid="picker-qr-toggle" onClick={() => { setQrMode((v) => !v); setScanMode(false); }}
                className={`inline-flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition ${qrMode ? "bg-[#0b5f58] text-white" : "bg-[#0f766e] text-white hover:bg-[#0b5f58]"}`}>
                <QrCode size={16} /> {qrMode ? "Stop camera" : "Scan bin QR to confirm"}
              </button>
              {qrMode && <QrScanner onDecode={onQrDecode} onClose={() => setQrMode(false)} />}
              <button type="button" data-testid="picker-scan-toggle" onClick={() => { setScanMode((v) => !v); setQrMode(false); }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#c8cfda] bg-white py-2 text-sm font-semibold text-[#0f766e] hover:border-[#0f766e]">
                <ScanLine size={16} /> {scanMode ? "Hide barcode scan" : "Scan bin barcode (handheld gun)"}
              </button>
              {scanMode && (
                <form data-testid="picker-scan-form" onSubmit={(e) => { e.preventDefault(); scanBin(scanValue); }} className="rounded-xl bg-[#f7f8fa] p-3">
                  <div className="text-xs uppercase tracking-wide text-[#89919a]">Scan or type the bin barcode</div>
                  <input data-testid="picker-scan-input" value={scanValue} autoFocus onChange={(e) => setScanValue(e.target.value)} placeholder={current.from_bin}
                    className="mt-2 w-full rounded-lg border border-[#c8cfda] bg-white px-3 py-2 text-center font-mono text-lg outline-none focus:border-[#0f766e]" />
                  <button data-testid="picker-scan-confirm" type="submit" className="mt-2 w-full rounded-lg bg-[#0f766e] py-2 text-sm font-semibold text-white hover:bg-[#0b5f58]">Confirm scanned bin</button>
                  <p className="mt-1 text-center text-[10px] text-[#89919a]">Handheld scanners type the code and press Enter automatically.</p>
                </form>
              )}
            </div>
            </>
          )}

          {/* STEP 2 — quantity */}
          {step === "qty" && (
            <div className="mt-5">
              <div className="mb-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#89919a]">Confirm product</div>
                {renderProductCard(false)}
              </div>
              <div className="rounded-xl bg-[#f7f8fa] p-4 text-center">
                <div className="text-xs uppercase tracking-wide text-[#89919a]">Pick quantity</div>
                <div data-testid="picker-qty" className="mt-1 text-4xl font-bold text-[#32363a]">{num(current.qty)} <span className="text-lg font-medium text-[#6a6d70]">{current.uom}</span></div>
                <div className="mt-1 font-mono text-[11px] text-[#89919a]">{current.material_code}</div>
              </div>
              <button data-testid="picker-confirm-qty" onClick={confirmQty}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#107e3e] py-3 text-base font-semibold text-white hover:bg-[#0c6a34]">
                <Check size={18} /> Confirm quantity
              </button>
            </div>
          )}

          {/* STEP 3 — next */}
          {step === "next" && (
            <div className="mt-5">
              <div className="mb-3">{renderProductCard(true)}</div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
                <PackageCheck size={28} className="mx-auto text-emerald-600" />
                <div className="mt-1 text-sm font-semibold text-emerald-700">Pick ready to complete</div>
                <div className="text-xs text-emerald-600">{num(current.qty)} {current.uom} · bin {current.from_bin}</div>
              </div>
              <button data-testid="picker-next" onClick={goNext}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#DB2777] py-3 text-base font-semibold text-white hover:bg-[#be1e63]">
                <ArrowRight size={18} /> Confirm &amp; next pick
              </button>
            </div>
          )}

          <div className="mt-3"><PromptToggle /></div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <button data-testid="picker-speak" onClick={() => speak(currentInstruction)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#e5e9f0] bg-[#eef1f5] py-2 text-sm text-[#32363a] hover:border-[#DB2777]">
              <Volume2 size={16} /> Repeat</button>
            <button data-testid="picker-skip" onClick={skip}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#c8cfda] bg-white py-2 text-sm font-semibold text-[#32363a] hover:border-[#DB2777]"><SkipForward size={16} /> Skip</button>
          </div>

          <button data-testid="picker-mic" onClick={toggleMic}
            className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3 text-base font-semibold text-white ${listening ? "bg-[#bb0000] animate-pulse" : "bg-[#DB2777] hover:bg-[#be1e63]"}`}>
            {listening ? <><MicOff size={18} /> Listening… {step === "loc" ? "say the 2 digits" : step === "qty" ? "say confirm" : "say next"}</> : <><Mic size={18} /> Voice command</>}</button>
          {heard && (
            <p data-testid="picker-heard" className="mt-2 text-center text-[11px] text-[#6a6d70]">Heard: “{heard.text}”{typeof heard.conf === "number" && heard.conf > 0 ? ` · ${Math.round(heard.conf * 100)}%` : ""}{parseDigits(heard.text) ? ` → ${parseDigits(heard.text)}` : ""}</p>
          )}
          <p className="mt-2 text-center text-[11px] text-[#89919a]">Voice works in Safari (iPhone) &amp; Chrome. Read both digits together, e.g. “four seven”. Buttons always work.</p>
          {inIframe && (
            <button data-testid="picker-open-tab" onClick={() => window.open(window.location.href, "_blank")} className="mt-1 w-full rounded-lg border border-[#c8cfda] bg-white py-2 text-xs font-semibold text-[#0f766e] hover:border-[#0f766e]">Open in full browser tab for mic access</button>
          )}
        </div>
      )}
    </div>
  );
}
